import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Package, Heart, ChevronRight, ShoppingBag, TrendingUp, 
  LogOut, Shield, ArrowLeft, ChevronDown, ChevronUp, Settings,
  Building2, Mail, RefreshCw, MapPin, Phone, Edit2, Save, X, Eye, Send, Clock, CheckCircle, XCircle,
  Download, Smartphone, MessageCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import CartSidebar from '../components/CartSidebar';
import BottomNavigation from '../components/BottomNavigation';
import PointsSection from '../components/PointsSection';
import WineClubCTA from '../components/WineClubCTA';
import axios from 'axios';
import { buildEditedOrderMessage, buildCompactOrderMessage, openWhatsappWithFallback } from '../utils/orderWhatsappMessage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Order Detail Modal — componente TOP-LEVEL (fuera de AccountPage) a propósito:
// antes estaba definido DENTRO de AccountPage, lo que hacía que React lo
// tratara como un tipo de componente NUEVO en cada re-render del padre
// (cada tecla escrita en el buscador de "agregar producto" reseteaba
// `editAddSearch` → re-render de AccountPage → nueva referencia de función
// → React desmontaba y volvía a montar TODO el modal, incluido el <input>,
// que perdía el foco). Bug reportado: "al poner una letra te saca". Con el
// componente a nivel de módulo, la identidad no cambia entre renders y el
// input mantiene el foco al tipear.
const OrderDetailModal = ({
  order, onClose, theme, formatDate, formatPrice, STATUS_MAP,
  editingOrder, editItems, editAddSearch, editAddResults, allProductsForEdit, allCombosForEdit,
  setEditAddSearch, setEditAddResults, updateEditItemQty, addProductToEdit, addComboToEdit,
  saveEditOrder, startEditOrder, setEditingOrder, setEditingOrderFull,
  confirmCancelOrderNum, setConfirmCancelOrderNum, handleCancelOrder,
  user, storeSettings, getEffectiveCustomerType,
  availableEditBranches, editAddBranch, setEditAddBranch,
}) => {
  const status = order.status || 'pendiente';
  const statusInfo = STATUS_MAP[status] || STATUS_MAP.pendiente;
  const canEdit = ['pendiente', 'confirmado'].includes(status);
  const isEditing = editingOrder === order.orderNumber;
  const displayItems = isEditing ? editItems : (order.items || order.cartItems || []);
  const displayTotal = isEditing ? editItems.reduce((s, it) => s + (it.totalPrice || (it.unitPrice || 0) * it.quantity), 0) : order.total;
  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={onClose} data-testid="order-detail-modal">
      {/* flex-col + footer separado del área con scroll: antes los botones
          "Guardar Cambios"/"Editar Pedido" quedaban DENTRO del bloque con
          scroll (max-h-[60vh]), así que al agregar productos con el buscador
          la lista crecía y el botón quedaba escondido más abajo sin que
          fuera obvio que había que scrollear. Bug reportado: "no encuentro
          el botón de enviar pedido editado". Ahora el footer con los
          botones queda SIEMPRE visible, fuera del área que scrollea. */}
      <div className="bg-white rounded-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0" style={{ backgroundColor: theme.primary }}>
          <h3 className="text-lg font-bold text-white">Pedido #{order.orderNumber}</h3>
          {order.order_type === 'vendedor' && <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">Vendedor</span>}
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm" style={{ color: theme.textSecondary }}>{formatDate(order.timestamp || order.date)}</p>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusInfo.color}`}>{statusInfo.label}</span>
          </div>

          <h4 className="font-medium mb-2">Productos:</h4>
          {order.order_type === 'vendedor' && order.seller_client_name && (
            <div className="mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200 text-sm">
              <p className="font-semibold text-amber-800">Cliente: {order.seller_client_name}</p>
              {order.seller_client_business && <p className="text-amber-700">{order.seller_client_business}</p>}
              {order.seller_client_city && <p className="text-amber-600">{order.seller_client_city}</p>}
            </div>
          )}
          <div className="space-y-2 mb-4">
            {displayItems.length > 0 ? (
              displayItems.map((item, idx) => {
                const isCombo = item.isCombo || item.comboId;
                const minQty = item.minimumQuantity || item.packSize || 1;
                return (
                <div key={idx} className={`flex justify-between items-center p-2 rounded ${isCombo ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50'}`}>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.productName || item.name} {isCombo && <span className="text-[10px] bg-purple-200 text-purple-700 px-1 rounded">COMBO</span>}</p>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      x{item.quantity} @ {formatPrice(item.unitPrice || item.price)}
                      {isEditing && !isCombo && minQty > 1 && <span className="text-amber-600"> (min: {minQty})</span>}
                      {(item.branchName || '').trim() && <span className="ml-1 text-slate-500">🏪 {item.branchName}</span>}
                    </p>
                  </div>
                  {isEditing && !isCombo ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateEditItemQty(idx, -1)} className="w-7 h-7 bg-slate-200 rounded font-bold text-sm">−</button>
                      <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                      <button onClick={() => updateEditItemQty(idx, 1)} className="w-7 h-7 rounded text-white font-bold text-sm" style={{ backgroundColor: theme.primary }}>+</button>
                    </div>
                  ) : isEditing && isCombo ? (
                    <span className="font-bold text-sm text-purple-700 px-2">{item.quantity}</span>
                  ) : (
                    <p className="font-bold text-sm" style={{ color: theme.primary }}>{formatPrice(item.totalPrice || (item.unitPrice || 0) * item.quantity)}</p>
                  )}
                </div>
                );
              })
            ) : (
              <p className="text-sm text-slate-500">Sin detalles disponibles</p>
            )}
            {isEditing && (
              <div className="pt-2 border-t">
                {/* Selector de sucursal para lo que se agregue a continuación.
                    Antes: los productos/combos agregados no llevaban sucursal
                    y el mensaje de WhatsApp los agrupaba bajo "Sin sucursal
                    asignada" (parecía una sucursal nueva creada solita).
                    Solo se muestra si el pedido o el cliente tienen sucursales. */}
                {availableEditBranches.length > 0 && (
                  <div className="mb-2">
                    <label className="text-xs font-medium text-slate-600 block mb-1">Sucursal para lo que agregues ahora:</label>
                    <select value={editAddBranch} onChange={e => setEditAddBranch(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:border-transparent transition-shadow"
                      style={{ '--tw-ring-color': theme.primary }}
                      data-testid="edit-order-branch-select">
                      {availableEditBranches.map(b => <option key={b} value={b}>{b}</option>)}
                      <option value="">Sin sucursal asignada</option>
                    </select>
                  </div>
                )}
                <input type="text" placeholder="Buscar producto o combo para agregar..." value={editAddSearch}
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                  onChange={e => {
                    const v = e.target.value;
                    setEditAddSearch(v);
                    if (v.length >= 2) {
                      const vLower = v.toLowerCase();
                      const comboResults = (allCombosForEdit || [])
                        .filter(c => c.active !== false && (c.name || '').toLowerCase().includes(vLower))
                        .slice(0, 3).map(c => ({ ...c, _kind: 'combo' }));
                      const prodResults = allProductsForEdit
                        .filter(p => p.activo && p.name.toLowerCase().includes(vLower))
                        .slice(0, 5).map(p => ({ ...p, _kind: 'product' }));
                      setEditAddResults([...comboResults, ...prodResults].slice(0, 6));
                    } else { setEditAddResults([]); }
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:ring-2 focus:border-transparent transition-shadow"
                  style={{ '--tw-ring-color': theme.primary }}
                  data-testid="edit-order-add-product" />
                {editAddResults.length > 0 && (
                  <div className="mt-1 border rounded-lg overflow-hidden">
                    {editAddResults.map(p => {
                      if (p._kind === 'combo') {
                        return (
                          <button key={`combo-${p.id}`} onClick={() => addComboToEdit(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 border-b last:border-b-0 flex justify-between items-center" data-testid={`edit-order-add-combo-${p.id}`}>
                            <span className="flex items-center gap-1">
                              <span className="text-[9px] font-bold bg-purple-200 text-purple-700 px-1 rounded">COMBO</span>
                              {p.name}
                            </span>
                            <span className="text-xs font-medium text-purple-700">{formatPrice(p.comboPrice)}</span>
                          </button>
                        );
                      }
                      const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
                      const _sPct = (_eff === 'consumidor_final' && storeSettings) ? (storeSettings.retailSurchargePercent || 0) : 0;
                      const _displayPrice = Math.ceil((p.priceNumeric || 0) * (1 + _sPct / 100));
                      return (
                        <button key={p.id} onClick={() => addProductToEdit(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b last:border-b-0 flex justify-between">
                          <span>{p.name}</span>
                          <span className="text-xs font-medium" style={{ color: theme.primary }}>{formatPrice(_displayPrice)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t pt-3">
            <div className="flex justify-between font-bold pt-2">
              <span>Total:</span>
              <span style={{ color: theme.primary }}>{formatPrice(displayTotal)}</span>
            </div>
          </div>

          {order.shippingMethodName && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm"><strong>Envío:</strong> {order.shippingMethodName}</p>
              {order.address && <p className="text-sm mt-1">{order.address}, {order.city}</p>}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="p-4 border-t space-y-2 flex-shrink-0 bg-white">
            {isEditing ? (
              <div className="flex gap-2">
                <button onClick={() => saveEditOrder(order.orderNumber)} className="flex-1 py-3 rounded-lg text-white font-bold active:scale-95 transition-transform" style={{ backgroundColor: theme.primary }} data-testid="save-edit-order-btn">
                  Guardar Cambios
                </button>
                <button onClick={() => { setEditingOrder(null); setEditingOrderFull(null); }} className="px-4 py-3 rounded-lg border border-slate-300 font-bold text-slate-600">
                  Cancelar
                </button>
              </div>
            ) : (
              <button onClick={() => startEditOrder(order)} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 font-bold active:scale-95 transition-transform" style={{ borderColor: theme.primary, color: theme.primary }} data-testid="edit-order-btn">
                <Edit2 size={18} /> Editar Pedido
              </button>
            )}
            {confirmCancelOrderNum === order.orderNumber ? (
              <div className="flex gap-2 w-full">
                <button onClick={() => handleCancelOrder(order.orderNumber)} className="flex-1 flex items-center justify-center gap-1 py-3 rounded-lg bg-red-600 text-white font-bold text-sm" data-testid="confirm-cancel-order-btn">
                  SI, cancelar
                </button>
                <button onClick={() => setConfirmCancelOrderNum(null)} className="flex-1 py-3 rounded-lg border border-slate-300 font-medium text-sm text-slate-600">
                  No
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirmCancelOrderNum(order.orderNumber)} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-red-50 text-red-600 font-bold active:scale-95 transition-transform" data-testid="cancel-order-btn">
                <XCircle size={18} /> Cancelar Pedido
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Component for upgrade request from consumidor_final to comercio
const UpgradeRequestSection = ({ user, theme }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [formData, setFormData] = useState({
    business_name: '',
    cuit: '',
    message: ''
  });

  useEffect(() => {
    fetchMyRequests();
  }, [user?.email]);

  const fetchMyRequests = async () => {
    if (!user?.email) return;
    try {
      const response = await axios.get(`${API}/upgrade-requests/my?email=${encodeURIComponent(user.email)}`);
      setMyRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name.trim()) {
      alert('Por favor ingresá el nombre de tu negocio');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/upgrade-requests`, {
        user_email: user.email,
        user_name: user.name || '',
        business_name: formData.business_name,
        cuit: formData.cuit,
        message: formData.message
      });
      alert('¡Solicitud enviada! Te avisaremos cuando sea revisada.');
      setShowForm(false);
      setFormData({ business_name: '', cuit: '', message: '' });
      fetchMyRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  const pendingRequest = myRequests.find(r => r.status === 'pending');
  const [confirmCancelUpgrade, setConfirmCancelUpgrade] = useState(false);
  const lastRequest = myRequests[0];

  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      {pendingRequest ? (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700">
            <Clock size={16} />
            <span className="text-sm font-medium">Solicitud pendiente de revisión</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">Negocio: {pendingRequest.business_name}</p>
          {confirmCancelUpgrade ? (
            <div className="flex gap-2 mt-2">
              <button
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/upgrade-requests/cancel?email=${encodeURIComponent(user.email)}`);
                    fetchMyRequests();
                  } catch (err) { alert('Error al cancelar la solicitud'); }
                  setConfirmCancelUpgrade(false);
                }}
                className="px-3 py-1 text-xs font-bold bg-red-600 text-white rounded-lg"
                data-testid="confirm-cancel-upgrade-btn"
              >SI, cancelar</button>
              <button onClick={() => setConfirmCancelUpgrade(false)} className="px-3 py-1 text-xs font-medium border border-slate-300 rounded-lg">No</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmCancelUpgrade(true)}
              className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              data-testid="cancel-upgrade-request-btn"
            >
              Cancelar solicitud y volver a enviar
            </button>
          )}
        </div>
      ) : lastRequest?.status === 'rejected' ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-2">
          <div className="flex items-center gap-2 text-red-700">
            <XCircle size={16} />
            <span className="text-sm font-medium">Solicitud rechazada</span>
          </div>
          <p className="text-xs text-red-600 mt-1">Podés volver a solicitar con más información</p>
        </div>
      ) : null}

      {!pendingRequest && (
        <>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}
            >
              <Building2 size={16} />
              Solicitar cuenta Comercio
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-sm font-medium" style={{ color: theme.text }}>Solicitar cambio a Comercio</p>
              <input
                type="text"
                placeholder="Nombre del negocio *"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                required
              />
              <input
                type="text"
                placeholder="CUIT (opcional)"
                value={formData.cuit}
                onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
              <textarea
                placeholder="Mensaje adicional (opcional)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2 px-4 border border-slate-300 rounded-lg text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 px-4 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2"
                  style={{ backgroundColor: theme.primary }}
                >
                  <Send size={14} />
                  {loading ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

// 🏪 Mis Sucursales — CRUD de sucursales del cliente B2B. Se persiste vía
// PUT /api/auth/branches y refresca el `user` para reflejar en el catálogo.
const MyBranchesSection = ({ user, theme, refreshUser }) => {
  const [branches, setBranches] = useState(Array.isArray(user?.branches) ? user.branches : []);
  const [newName, setNewName] = useState('');
  // Empezamos EXPANDIDO por defecto para que el botón "Agregar" sea visible
  // sin necesidad de tocar el toggle. Cerrar sólo si el user lo hace explícito.
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    setBranches(Array.isArray(user?.branches) ? user.branches : []);
  }, [user?.branches]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/branches`, { branches: next }, { withCredentials: true });
      const applied = r?.data?.branches || next;
      setBranches(applied);
      setSavedMsg('Cambios guardados');
      setTimeout(() => setSavedMsg(''), 2000);
      if (typeof refreshUser === 'function') refreshUser();
      return true;
    } catch (e) {
      setSavedMsg('No se pudo guardar. Reintentá.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const clean = (newName || '').trim();
    if (!clean) return;
    if (branches.some(b => b.toLowerCase() === clean.toLowerCase())) {
      setNewName('');
      return;
    }
    const next = [...branches, clean];
    const ok = await save(next);
    if (ok) setNewName('');
  };

  const remove = async (name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    const next = branches.filter(b => b.toLowerCase() !== name.toLowerCase());
    await save(next);
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border" style={{ borderColor: theme.border }} data-testid="my-branches-section">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-4 text-left"
        data-testid="my-branches-toggle"
      >
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.primary}15` }}>
          <span className="text-2xl">🏪</span>
        </div>
        <div className="flex-1">
          <p className="font-bold" style={{ color: theme.text }}>Mis Sucursales</p>
          <p className="text-xs" style={{ color: theme.textSecondary }}>
            {branches.length === 0
              ? 'Cargá tus puntos de venta y separá el pedido por sucursal al comprar.'
              : `${branches.length} sucursal${branches.length === 1 ? '' : 'es'} guardada${branches.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <span className="text-slate-400 text-xl" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
      </button>
      {expanded && (
        <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: theme.border }} data-testid="my-branches-expanded">
          {branches.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">
              Todavía no cargaste ninguna sucursal.
            </p>
          )}
          {branches.map((b, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2" data-testid={`my-branches-item-${idx}`}>
              <span className="text-orange-700 text-lg" aria-hidden>🏪</span>
              <span className="flex-1 text-sm font-semibold text-slate-800 truncate" data-testid={`my-branches-name-${idx}`}>{b}</span>
              <button
                type="button"
                onClick={() => remove(b)}
                className="text-red-400 hover:text-red-600 text-xs font-bold"
                data-testid={`my-branches-delete-${idx}`}
                aria-label={`Eliminar ${b}`}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
              placeholder="Ej: Kiosco Ori 1"
              maxLength={60}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="my-branches-new-input"
            />
            <button
              type="button"
              onClick={add}
              disabled={saving || !newName.trim()}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: theme.primary }}
              data-testid="my-branches-add-btn"
            >
              {saving ? 'Guardando…' : 'Agregar'}
            </button>
          </div>
          {savedMsg && <p className="text-[11px] text-emerald-600 font-medium">{savedMsg}</p>}
          <p className="text-[10px] text-slate-400 leading-relaxed">
            💡 Al comprar, elegí una sucursal en el header del catálogo. Los productos se etiquetan automáticamente y podés armar un pedido único agrupado por sucursal en el mensaje de WhatsApp.
          </p>
        </div>
      )}
    </div>
  );
};



const AccountPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, loginWithGoogle, logout, loading: authLoading, storeSettings, refreshUser, getEffectiveCustomerType, getFinalUnitPrice, getBestCartDiscountPct } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showMyData, setShowMyData] = useState(false);
  const [editingData, setEditingData] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sellerRequest, setSellerRequest] = useState(null);
  const [showSellerForm, setShowSellerForm] = useState(false);
  const [sellerFormData, setSellerFormData] = useState({ phone: '', cbu: '', message: '' });
  const [sellerFormLoading, setSellerFormLoading] = useState(false);
  const [confirmCancelSellerReq, setConfirmCancelSellerReq] = useState(false);
  const [userData, setUserData] = useState({
    name: '',
    phone: '',
    address: '',
    addressNumber: '',
    city: '',
    businessName: '',
    customerType: 'consumidor_final',
    cuit: '',
    condicionIva: 'Consumidor final',
  });
  const [editData, setEditData] = useState({});
  const [deliveryCities, setDeliveryCities] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    favoriteProducts: [],
    lastOrderDate: null
  });
  const [accountSummary, setAccountSummary] = useState(null);  // cuenta corriente histórica
  const [showAccount, setShowAccount] = useState(false);

  // PWA install handling
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // SECURITY: Mi Cuenta requiere sesión real. Si un guest (sin login)
  // llega acá por URL directa, lo mandamos al inicio para que pase
  // por el WelcomeGate / Login.
  useEffect(() => {
    if (authLoading) return;
    const isLocalAdmin = typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true';
    if (!user && !isLocalAdmin) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadUserData();
    loadOrderHistory();
    checkAdminStatus();
    checkSellerStatus();
    // Cargar ciudades de reparto disponibles
    axios.get(`${API}/delivery-cities`).then(r => setDeliveryCities(r.data?.cities || [])).catch(() => {});
    // Cargar resumen de cuenta corriente histórica si está vinculado a un legacy
    if (user) {
      axios.get(`${API}/user/account-summary`, { withCredentials: true })
        .then(r => setAccountSummary(r.data))
        .catch(() => setAccountSummary(null));
    }
  }, [user]);

  const checkSellerStatus = async () => {
    if (!user) return;
    // Check if user is a seller (by role field)
    if (user.role === 'seller') {
      setIsSeller(true);
    }
    // Check for pending seller request
    try {
      const response = await axios.get(`${API}/seller-requests/my`, { withCredentials: true });
      setSellerRequest(response.data);
    } catch (error) {
      // No pending request
    }
  };

  const handleSellerRequest = async (e) => {
    e.preventDefault();
    if (!sellerFormData.phone.trim()) {
      alert('Por favor ingresá tu teléfono');
      return;
    }
    setSellerFormLoading(true);
    try {
      await axios.post(`${API}/seller-requests`, sellerFormData, { withCredentials: true });
      alert('¡Solicitud enviada! Te avisaremos cuando sea revisada.');
      setShowSellerForm(false);
      checkSellerStatus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSellerFormLoading(false);
    }
  };

  const loadUserData = () => {
    // Priority: user object from DB > localStorage fallback
    if (user) {
      const dbData = {
        name: user.name || user.firstName || '',
        phone: user.phone || '',
        address: user.address || '',
        addressNumber: user.addressNumber || '',
        city: user.city || '',
        businessName: user.businessName || '',
        customerType: user.customer_type || user.customerType || 'consumidor_final',
        cuit: user.cuit || '',
        condicionIva: user.condicionIva || (user.customer_type === 'comercio' ? 'Responsable Inscripto' : 'Consumidor final'),
      };
      // Para usuarios logueados, sólo completamos campos de ENVÍO faltantes
      // (phone/address/city) desde localStorage. Los campos de IDENTIDAD
      // (businessName, customerType, cuit, condicionIva) provienen únicamente
      // de la cuenta del user — nunca del guest state, para evitar que la
      // sesión Google quede contaminada con datos de comercio guest.
      const savedData = localStorage.getItem('bebida-bacana-shipping');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          dbData.name = dbData.name || parsed.customerName || '';
          dbData.phone = dbData.phone || parsed.phone || '';
          dbData.address = dbData.address || parsed.address || '';
          dbData.city = dbData.city || parsed.city || '';
          // NO: dbData.businessName se queda solo con lo del user record
        } catch { /* localStorage corrupto, ignoramos */ }
      }
      setUserData(dbData);
      return;
    }
    const savedData = localStorage.getItem('bebida-bacana-shipping');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setUserData({
          name: parsed.customerName || '',
          phone: parsed.phone || '',
          address: parsed.address || '',
          city: parsed.city || '',
          businessName: parsed.businessName || ''
        });
      } catch { /* ignore */ }
    }
  };

  const loadOrderHistory = async () => {
    // If user is logged in, fetch from API (includes status)
    if (user?.email) {
      try {
        const response = await axios.get(`${API}/my-orders`, { withCredentials: true });
        const apiOrders = response.data;
        apiOrders.sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));
        setOrders(apiOrders);
        
        if (apiOrders.length > 0) {
          const totalSpent = apiOrders.reduce((sum, order) => sum + (order.total || 0), 0);
          setStats({
            totalOrders: apiOrders.length,
            totalSpent,
            favoriteProducts: [],
            lastOrderDate: apiOrders[0]?.timestamp ? new Date(apiOrders[0].timestamp) : null
          });
        }
        return;
      } catch (error) {
        console.error('Error fetching orders from API:', error);
      }
    }
    
    // Fallback to localStorage for guest users
    const orderHistory = JSON.parse(localStorage.getItem('bebida-bacana-orders') || '[]');
    orderHistory.sort((a, b) => new Date(b.date || b.timestamp) - new Date(a.date || a.timestamp));
    setOrders(orderHistory);
    
    if (orderHistory.length > 0) {
      const totalSpent = orderHistory.reduce((sum, order) => sum + (order.total || 0), 0);
      setStats({
        totalOrders: orderHistory.length,
        totalSpent,
        favoriteProducts: [],
        lastOrderDate: orderHistory[0]?.date ? new Date(orderHistory[0].date) : null
      });
    }
  };

  const checkAdminStatus = () => {
    const adminLoggedIn = localStorage.getItem('bebida-bacana-admin') === 'true';
    setIsAdmin(adminLoggedIn);
  };

  const handleAdminLogin = () => navigate('/admin/login');
  const handleAdminLogout = () => {
    localStorage.removeItem('bebida-bacana-admin');
    setIsAdmin(false);
  };

  const startEditing = () => {
    setEditData({ ...userData });
    setEditingData(true);
  };

  const [savingProfile, setSavingProfile] = useState(false);
  const saveUserData = async () => {
    // Save to localStorage for quick access during ordering
    const savedData = JSON.parse(localStorage.getItem('bebida-bacana-shipping') || '{}');
    const newData = {
      ...savedData,
      customerName: editData.name,
      phone: editData.phone,
      address: editData.address,
      addressNumber: editData.addressNumber || '',
      city: editData.city,
      businessName: editData.businessName,
      customerType: editData.customerType,
      cuit: editData.cuit,
      condicionIva: editData.condicionIva,
    };
    localStorage.setItem('bebida-bacana-shipping', JSON.stringify(newData));
    setUserData(editData);
    
    // Also persist to DB if user is logged in
    if (user) {
      setSavingProfile(true);
      try {
        await axios.put(`${API}/user/profile`, {
          firstName: editData.name,
          phone: editData.phone,
          address: editData.address,
          addressNumber: editData.addressNumber || '',
          city: editData.city,
          businessName: editData.businessName,
          customerType: editData.customerType,
          cuit: editData.cuit,
          condicionIva: editData.condicionIva,
        }, { withCredentials: true });
        if (typeof refreshUser === 'function') refreshUser();
      } catch (error) {
        const msg = error.response?.data?.detail || error.message;
        alert('No se pudieron guardar tus datos: ' + msg);
        console.error('Error saving profile to DB:', error);
        setSavingProfile(false);
        return;
      } finally {
        setSavingProfile(false);
      }
    }
    setEditingData(false);
  };

  const formatPrice = (price) => `$ ${price?.toLocaleString('es-AR') || 0}`;
  const formatDate = (date) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const adminMenuItems = [
    { icon: Settings, label: 'Panel de Administración', description: 'Productos, banners y más', action: () => navigate('/admin/dashboard'), color: theme.primary },
    { icon: Building2, label: 'Configuración de Tienda', description: 'Precios y datos', action: () => navigate('/admin/store'), color: '#9B59B6' }
  ];

  const displayName = user?.name || userData.name || 'Usuario';
  const displayEmail = user?.email || '';

  // Cancel order handler
  const [confirmCancelOrderNum, setConfirmCancelOrderNum] = useState(null);
  const handleCancelOrder = async (orderNumber) => {
    try {
      const res = await axios.put(`${API}/my-orders/${orderNumber}/cancel`, {}, { withCredentials: true });
      alert('Pedido cancelado exitosamente');
      if (res.data?.whatsapp_message) {
        const waNumber = res.data.whatsapp_number || '543571522400';
        const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(res.data.whatsapp_message)}`;
        window.open(waUrl, '_blank');
      }
      loadOrderHistory();
      setSelectedOrder(null);
      setConfirmCancelOrderNum(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cancelar el pedido');
    }
  };

  // Edit order handler
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingOrderFull, setEditingOrderFull] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editAddSearch, setEditAddSearch] = useState('');
  const [editAddResults, setEditAddResults] = useState([]);
  const [allProductsForEdit, setAllProductsForEdit] = useState([]);
  const [allCombosForEdit, setAllCombosForEdit] = useState([]);
  // 🏪 Sucursal seleccionada para los productos/combos que se agreguen
  // mientras se edita el pedido. Antes, los productos agregados no llevaban
  // `branchName` y el mensaje de WhatsApp los agrupaba bajo "Sin sucursal
  // asignada" — parecía una sucursal nueva creada de la nada. Ahora, si el
  // pedido (o el cliente) tiene sucursales, se pide elegir a cuál va cada
  // producto nuevo antes de agregarlo.
  const [editAddBranch, setEditAddBranch] = useState('');

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setAllProductsForEdit(r.data)).catch(() => {});
    axios.get(`${API}/combos`, { withCredentials: true }).then(r => setAllCombosForEdit(r.data)).catch(() => {});
  }, []);

  // Sucursales relevantes para este pedido: las que ya tienen productos +
  // las que el cliente tiene registradas en "Mis Sucursales".
  const availableEditBranches = useMemo(() => {
    const set = new Set((editItems || []).map(it => (it.branchName || '').trim()).filter(Boolean));
    (user?.branches || []).forEach(b => { if (b && b.trim()) set.add(b.trim()); });
    return Array.from(set);
  }, [editItems, user]);

  const startEditOrder = (order) => {
    const items = (order.items || []).map(it => ({ ...it }));
    setEditItems(recalcEditItemsPricing(items));
    setEditingOrder(order.orderNumber);
    setEditingOrderFull(order);
    setEditAddSearch(''); setEditAddResults([]);
    const orderBranches = Array.from(new Set(items.map(it => (it.branchName || '').trim()).filter(Boolean)));
    const userBranches = (user?.branches || []).map(b => (b || '').trim()).filter(Boolean);
    const allBranches = Array.from(new Set([...orderBranches, ...userBranches]));
    setEditAddBranch(allBranches[0] || '');
  };

  // 💰 Recalcula el precio de cada línea (no-combo) usando el MISMO motor de
  // descuentos que el catálogo/carrito (getFinalUnitPrice: aplica descuento
  // por volumen del carrito, descuento permanente y tiers por cantidad).
  // Antes, al agregar un producto en la edición de un pedido, se usaba
  // SOLO el precio base (+ recargo consumidor final si aplica), ignorando
  // por completo cualquier descuento — este helper corrige eso para TODAS
  // las líneas cada vez que el pedido editado cambia (agregar, sumar/restar
  // cantidad), ya que el total del carrito puede cruzar un nuevo tramo de
  // descuento y beneficiar a TODOS los productos, no solo al que se tocó.
  // También guarda `listUnitPrice` (precio de LISTA, sin descuento) en cada
  // línea: lo necesita el mensaje de WhatsApp para mostrar precio de lista
  // por línea + un único descuento agregado, igual que un pedido nuevo.
  const recalcEditItemsPricing = (items) => {
    if (!getFinalUnitPrice || allProductsForEdit.length === 0) return items;
    const rawTotal = items.reduce((sum, it) => {
      if (it.isCombo || it.comboId) return sum + (it.totalPrice || 0);
      const prod = allProductsForEdit.find(p => p.id === (it.productId || it.id));
      const base = prod ? Number(prod.priceNumeric || 0) : Number(it.unitPrice || it.price || 0);
      return sum + base * it.quantity;
    }, 0);
    return items.map(it => {
      if (it.isCombo || it.comboId) return it; // combos no llevan descuento de carrito
      const prod = allProductsForEdit.find(p => p.id === (it.productId || it.id));
      if (!prod) return it;
      const finalUnit = getFinalUnitPrice(prod, rawTotal, it.quantity, null);
      return { ...it, unitPrice: finalUnit, price: finalUnit, totalPrice: finalUnit * it.quantity, listUnitPrice: Number(prod.priceNumeric || 0) };
    });
  };

  const updateEditItemQty = (idx, delta) => {
    setEditItems(prev => {
      const updated = prev.map((it, i) => {
        if (i !== idx) return it;
        if (it.isCombo || it.comboId) return it; // Don't edit combos
        const minQty = it.minimumQuantity || it.packSize || 1;
        const step = Math.max(1, minQty);
        const newQty = it.quantity + (delta > 0 ? step : -step);
        if (newQty < minQty) return null; // Remove if below minimum
        return { ...it, quantity: newQty, totalPrice: (it.unitPrice || it.price || 0) * newQty };
      }).filter(Boolean);
      return recalcEditItemsPricing(updated);
    });
  };

  const addProductToEdit = (product) => {
    const existing = editItems.findIndex(it => (it.productId || it.id) === product.id && !it.comboId && (it.branchName || '') === (editAddBranch || ''));
    let updated;
    if (existing >= 0) {
      const minQty = product.minimumQuantity || product.packSize || 1;
      updated = editItems.map((it, i) => i === existing ? { ...it, quantity: it.quantity + Math.max(1, minQty) } : it);
    } else {
      const minQty = product.minimumQuantity || product.packSize || 1;
      updated = [...editItems, {
        productId: product.id, productName: product.name, name: product.name,
        unitPrice: product.priceNumeric, price: product.priceNumeric,
        quantity: minQty, totalPrice: product.priceNumeric * minQty,
        minimumQuantity: product.minimumQuantity, packSize: product.packSize,
        branchName: editAddBranch || undefined,
      }];
    }
    setEditItems(recalcEditItemsPricing(updated));
    setEditAddSearch(''); setEditAddResults([]);
  };

  // 🎁 Agrega un COMBO completo (todas sus líneas) al pedido en edición,
  // con el mismo cálculo de precio con descuento que usa el carrito al
  // agregar un combo nuevo (ver `addComboToCart` en CartContext.js).
  const addComboToEdit = (combo) => {
    if (!combo?.products?.length) return;
    const discountFactor = combo.originalTotal > 0 ? combo.comboPrice / combo.originalTotal : 1;
    const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
    const surchargePct = (_eff === 'consumidor_final' && storeSettings) ? (storeSettings.retailSurchargePercent || 0) : 0;
    const surchargeFactor = 1 + surchargePct / 100;
    let updated = [...editItems];
    combo.products.forEach(cp => {
      const unitPrice = combo.pricingMode === 'manual'
        ? Math.round((cp.comboUnitPrice ?? cp.originalPrice) * surchargeFactor / 10) * 10
        : Math.round((cp.originalPrice || 0) * surchargeFactor * discountFactor / 10) * 10;
      const existingIdx = updated.findIndex(it => it.comboId === combo.id && it.productId === cp.productId && (it.branchName || '') === (editAddBranch || ''));
      if (existingIdx >= 0) {
        const newQty = updated[existingIdx].quantity + cp.quantity;
        updated = updated.map((it, i) => i === existingIdx ? { ...it, quantity: newQty, totalPrice: unitPrice * newQty } : it);
      } else {
        const label = `${cp.name} (${combo.name})${cp.bonificado ? ' BONIF' : ''}`;
        updated.push({
          productId: cp.productId, productName: label, name: label,
          unitPrice: unitPrice, price: unitPrice,
          quantity: cp.quantity, totalPrice: unitPrice * cp.quantity,
          isComboItem: true, comboId: combo.id, comboName: combo.name,
          branchName: editAddBranch || undefined,
        });
      }
    });
    setEditItems(recalcEditItemsPricing(updated));
    setEditAddSearch(''); setEditAddResults([]);
  };

  const saveEditOrder = async (orderNumber) => {
    try {
      const res = await axios.put(`${API}/my-orders/${orderNumber}/edit`, { items: editItems }, { withCredentials: true });
      alert('Pedido editado exitosamente');
      // 📲 El mensaje de WhatsApp se construye en el FRONTEND con el mismo
      // formato que un pedido NUEVO (agrupado por sucursal, precio de lista
      // por línea + descuento por objetivo agregado) en vez de usar el
      // resumen simplificado que devolvía el backend.
      const order = editingOrderFull || {};
      const rawTotal = editItems.reduce((sum, it) => sum + (it.isGift ? 0 : Number(it.listUnitPrice ?? it.unitPrice ?? it.price ?? 0) * (it.quantity || 0)), 0);
      const cartDiscountPct = getBestCartDiscountPct ? getBestCartDiscountPct(rawTotal) : 0;
      const permanentDiscountPct = Number(user?.permanentDiscountPct || 0);
      const { message: fullMessage, finalTotal } = buildEditedOrderMessage({
        orderNumber,
        customerName: order.customerName || user?.name || '',
        address: order.address || '',
        city: order.city || '',
        phone: order.phone || '',
        items: editItems,
        permanentDiscountPct,
        cartDiscountPct,
        shippingMethodName: order.shippingMethodName || '',
        shippingCost: order.shippingCost || 0,
      });
      const compactMessage = buildCompactOrderMessage({
        orderNumber,
        customerName: order.customerName || user?.name || '',
        address: order.address || '',
        city: order.city || '',
        phone: order.phone || '',
        items: editItems,
        finalTotal,
      });
      const waNumber = res.data?.whatsapp_number || '543571522400';
      openWhatsappWithFallback({ fullMessage, compactMessage, whatsappNumber: waNumber });
      setEditingOrder(null);
      setEditingOrderFull(null);
      loadOrderHistory();
      setSelectedOrder(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al editar el pedido');
    }
  };

  // Install app handler - listen for prompt actively
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(window.__pwaPrompt || null);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);

  useEffect(() => {
    // If prompt already captured globally, use it
    if (window.__pwaPrompt) setInstallPrompt(window.__pwaPrompt);
    
    // Listen for new prompt events
    const handler = (e) => {
      e.preventDefault();
      window.__pwaPrompt = e;
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    const prompt = installPrompt || window.__pwaPrompt;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          window.__pwaPrompt = null;
          setInstallPrompt(null);
        }
      } catch (err) {
        // prompt already used, show fallback
        setShowInstallModal(true);
      }
    } else {
      // No prompt available - show brief instructions
      setShowInstallModal(true);
    }
  };

  const STATUS_MAP = {
    pendiente:  { label: 'Pendiente',  color: 'bg-amber-100 text-amber-700', icon: Clock },
    confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    preparando: { label: 'Preparando', color: 'bg-indigo-100 text-indigo-700', icon: Package },
    enviado:    { label: 'Enviado',    color: 'bg-cyan-100 text-cyan-700', icon: Package },
    entregado:  { label: 'Entregado',  color: 'bg-green-100 text-green-700', icon: CheckCircle },
    cancelado:  { label: 'Cancelado',  color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  // iOS/Android Install Guide Modal
  const InstallGuideModal = () => (
    <div className="fixed inset-0 z-[100] flex items-end justify-center" data-testid="install-guide-modal">
      <div className="fixed inset-0 bg-black/50" onClick={() => setShowInstallModal(false)} />
      <div className="relative bg-white w-full max-w-md rounded-t-2xl shadow-2xl p-6">
        <button onClick={() => setShowInstallModal(false)} className="absolute top-3 right-3 p-2 hover:bg-slate-100 rounded-full">
          <X size={20} className="text-slate-400" />
        </button>
        <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">
          Instalá Bebida Bacana
        </h3>
        {isIOS ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <p className="text-sm">Abrí en <strong>Safari</strong> y tocá <strong>Compartir</strong> (ícono de cuadrado con flecha)</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <p className="text-sm">Tocá <strong>Agregar a inicio</strong></p>
            </div>
          </div>
        ) : isAndroid ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
              <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
              <p className="text-sm">Abrí en <strong>Google Chrome</strong></p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
              <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <p className="text-sm">Tocá los <strong>3 puntos ⋮</strong> arriba a la derecha</p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
              <p className="text-sm">Tocá <strong>"Instalar aplicación"</strong> o <strong>"Agregar a inicio"</strong></p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-600 text-center">Abrí esta página en <strong>Google Chrome</strong> en tu celular para instalar la app.</p>
        )}
        <button onClick={() => setShowInstallModal(false)} className="w-full mt-5 py-3 rounded-xl bg-emerald-600 text-white font-bold">
          Entendido
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-16 md:pb-0" style={{ backgroundColor: theme.background }}>
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder} onClose={() => setSelectedOrder(null)}
          theme={theme} formatDate={formatDate} formatPrice={formatPrice} STATUS_MAP={STATUS_MAP}
          editingOrder={editingOrder} editItems={editItems} editAddSearch={editAddSearch}
          editAddResults={editAddResults} allProductsForEdit={allProductsForEdit} allCombosForEdit={allCombosForEdit}
          setEditAddSearch={setEditAddSearch} setEditAddResults={setEditAddResults}
          updateEditItemQty={updateEditItemQty} addProductToEdit={addProductToEdit} addComboToEdit={addComboToEdit}
          saveEditOrder={saveEditOrder} startEditOrder={startEditOrder}
          setEditingOrder={setEditingOrder} setEditingOrderFull={setEditingOrderFull}
          confirmCancelOrderNum={confirmCancelOrderNum} setConfirmCancelOrderNum={setConfirmCancelOrderNum}
          handleCancelOrder={handleCancelOrder}
          user={user} storeSettings={storeSettings} getEffectiveCustomerType={getEffectiveCustomerType}
          availableEditBranches={availableEditBranches} editAddBranch={editAddBranch} setEditAddBranch={setEditAddBranch}
        />
      )}
      {showInstallModal && <InstallGuideModal />}
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft size={22} style={{ color: theme.text }} />
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>Mi Cuenta</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* User Profile Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ borderColor: theme.border }}>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={displayName} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold" style={{ backgroundColor: theme.primary }}>
                {displayName ? displayName.charAt(0).toUpperCase() : <User size={28} />}
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-bold" style={{ color: theme.text }}>{displayName}</h2>
              {displayEmail && (
                <p className="text-sm flex items-center gap-1" style={{ color: theme.textSecondary }}>
                  <Mail size={14} />{displayEmail}
                </p>
              )}
              {user?.customer_type && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                  {user.role === 'seller' ? <><TrendingUp size={12} />Vendedor</> : user.customer_type === 'comercio' ? <><Building2 size={12} />Comercio</> : <><User size={12} />Consumidor Final</>}
                </span>
              )}
            </div>
            {isAdmin && <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-white">Admin</span>}
          </div>

          {/* Estado del visitante (sin login) o login Google opcional */}
          {!user ? (
            <div className="mt-4 space-y-3">
              {(() => {
                const guestType = typeof window !== 'undefined' ? localStorage.getItem('bebida-bacana-guest-type') : null;
                const isComercio = guestType === 'comercio';
                const businessName = typeof window !== 'undefined' ? localStorage.getItem('bebida-bacana-guest-business') : '';
                return (
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-sm font-medium mb-2" style={{ color: theme.text }}>Tipo de cuenta:</p>
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white">
                      {isComercio ? <Building2 size={22} className="text-slate-700" /> : <User size={22} className="text-slate-600" />}
                      <div>
                        <p className="font-bold" style={{ color: theme.text }}>
                          {isComercio ? (businessName || 'Comercio') : 'Consumidor Final'}
                        </p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>
                          {isComercio ? 'Precios mayoristas' : 'Pedido mínimo según condiciones'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-sm font-medium mb-2" style={{ color: theme.text }}>Tipo de cuenta:</p>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white">
                  {user.customer_type === 'comercio' ? (
                    <>
                      <Building2 size={22} className="text-slate-700" />
                      <div>
                        <p className="font-bold" style={{ color: theme.text }}>Comercio</p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>Precios mayoristas</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <User size={22} className="text-slate-600" />
                      <div>
                        <p className="font-bold" style={{ color: theme.text }}>Consumidor Final</p>
                        {storeSettings?.retailSurchargePercent > 0 && (
                          <p className="text-xs" style={{ color: theme.textSecondary }}>
                            Precios para consumidor final
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {user.customer_type === 'consumidor_final' && (
                  <UpgradeRequestSection user={user} theme={theme} />
                )}

                {/* Club del Vino Bacano — CTA solo para consumidor final */}
                <div className="mt-3">
                  <WineClubCTA user={user} />
                </div>

                {/* Seller Section - shown when user has role 'seller' or 'admin' */}
                {(user.role === 'seller' || user.role === 'admin') && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <button
                      onClick={() => navigate('/seller')}
                      className="w-full p-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 transition-colors"
                      data-testid="goto-seller-dashboard-btn"
                    >
                      <TrendingUp size={18} />
                      Ir a Mi Panel de Vendedor
                    </button>
                  </div>
                )}
                
                {/* Request to become seller - for comercio and consumidor_final who are not already sellers */}
                {user.role !== 'seller' && user.role !== 'admin' && !isSeller && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    {sellerRequest?.status === 'pending' ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700">
                          <Clock size={16} />
                          <span className="text-sm font-medium">Solicitud de vendedor pendiente</span>
                        </div>
                        {confirmCancelSellerReq ? (
                          <div className="flex gap-2 mt-2">
                            <button onClick={async () => {
                              try {
                                await axios.delete(`${API}/seller-requests/cancel`, { withCredentials: true });
                                setSellerRequest(null);
                                checkSellerStatus();
                              } catch (err) { alert('Error al cancelar la solicitud'); }
                              setConfirmCancelSellerReq(false);
                            }} className="px-3 py-1 text-xs font-bold bg-red-600 text-white rounded-lg" data-testid="confirm-cancel-seller-req-btn">SI, cancelar</button>
                            <button onClick={() => setConfirmCancelSellerReq(false)} className="px-3 py-1 text-xs font-medium border border-slate-300 rounded-lg">No</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmCancelSellerReq(true)}
                            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                            data-testid="cancel-seller-request-btn"
                          >
                            Cancelar solicitud y volver a enviar
                          </button>
                        )}
                      </div>
                    ) : sellerRequest?.status === 'rejected' ? (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700">
                          <XCircle size={16} />
                          <span className="text-sm font-medium">Solicitud rechazada</span>
                        </div>
                      </div>
                    ) : showSellerForm ? (
                      <form onSubmit={handleSellerRequest} className="space-y-3">
                        <p className="text-sm text-slate-600 mb-2">
                          Como vendedor podrás tomar pedidos para comercios y ganar comisiones.
                        </p>
                        <input
                          type="tel"
                          value={sellerFormData.phone}
                          onChange={(e) => setSellerFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="Tu teléfono *"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          required
                        />
                        <input
                          type="text"
                          value={sellerFormData.cbu}
                          onChange={(e) => setSellerFormData(prev => ({ ...prev, cbu: e.target.value }))}
                          placeholder="CBU para comisiones (opcional)"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          maxLength={22}
                        />
                        <textarea
                          value={sellerFormData.message}
                          onChange={(e) => setSellerFormData(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="¿Por qué querés ser vendedor? (opcional)"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setShowSellerForm(false)}
                            className="flex-1 py-2 border border-slate-300 rounded-lg text-slate-600"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={sellerFormLoading}
                            className="flex-1 py-2 text-white rounded-lg font-medium"
                            style={{ backgroundColor: '#10B981' }}
                            data-testid="submit-seller-request-btn"
                          >
                            {sellerFormLoading ? 'Enviando...' : 'Enviar'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setShowSellerForm(true)}
                        className="w-full p-3 border-2 border-dashed rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-50"
                        style={{ borderColor: '#10B981', color: '#10B981' }}
                        data-testid="request-seller-btn"
                      >
                        <TrendingUp size={16} />
                        ¿Querés ser vendedor colaborador?
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button onClick={logout} className="w-full flex items-center justify-center gap-2 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                <LogOut size={16} />Cerrar sesión
              </button>
            </div>
          )}
        </div>

        {/* 🏪 Mis Sucursales — B2B + Admin (para preview/QA). */}
        {user && (user.customer_type === 'comercio' || user.role === 'admin') && (
          <MyBranchesSection user={user} theme={theme} refreshUser={refreshUser} />
        )}

        {/* Ver Lista de Precios — flag `priceListDownload` en Gestión de Módulos */}
        {user && (storeSettings?.featureFlags?.priceListDownload !== false) && (
          <button
            onClick={() => navigate('/mi-cuenta/lista-precios')}
            className="w-full bg-white rounded-xl p-4 shadow-sm border flex items-center gap-4 hover:bg-slate-50 transition-colors"
            style={{ borderColor: theme.border }}
            data-testid="download-price-list-btn"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.primary}15` }}>
              <Download size={22} style={{ color: theme.primary }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold" style={{ color: theme.text }}>Lista de Precios</p>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Ver precios por objetivo de compra y descargar PDF</p>
            </div>
            <ChevronRight size={20} style={{ color: theme.textSecondary }} />
          </button>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.primary}20` }}>
                <ShoppingBag size={20} style={{ color: theme.primary }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>{stats.totalOrders}</p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>Pedidos</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border" style={{ borderColor: theme.border }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.accent}20` }}>
                <TrendingUp size={20} style={{ color: theme.accent }} />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>{formatPrice(stats.totalSpent)}</p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>Total gastado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Puntos y Referidos */}
        {user && <PointsSection user={user} />}

        {/* Mis Datos Section */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: theme.border }}>
          <button onClick={() => setShowMyData(!showMyData)} className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.accent}15` }}>
              <MapPin size={20} style={{ color: theme.accent }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: theme.text }}>Mis Datos</p>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Dirección, teléfono y más</p>
            </div>
            {showMyData ? <ChevronUp size={20} style={{ color: theme.textSecondary }} /> : <ChevronDown size={20} style={{ color: theme.textSecondary }} />}
          </button>
          
          {showMyData && (
            <div className="border-t p-4" style={{ borderColor: theme.border }}>
              {editingData ? (
                <div className="space-y-3">
                  {/* Tipo de cliente */}
                  <div>
                    <label className="text-sm font-medium block mb-1">Tipo de cuenta *</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setEditData({ ...editData, customerType: 'consumidor_final', condicionIva: 'Consumidor final' })}
                        className={`px-3 py-2 rounded-lg border text-sm font-bold ${editData.customerType !== 'comercio' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
                        data-testid="customer-type-particular">
                        Particular
                      </button>
                      <button type="button" onClick={() => setEditData({ ...editData, customerType: 'comercio', condicionIva: editData.condicionIva && editData.condicionIva !== 'Consumidor final' ? editData.condicionIva : 'Responsable Inscripto' })}
                        className={`px-3 py-2 rounded-lg border text-sm font-bold ${editData.customerType === 'comercio' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
                        data-testid="customer-type-comercio">
                        Comercio
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Nombre</label>
                    <input type="text" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1" />
                  </div>

                  {editData.customerType === 'comercio' && (
                    <>
                      <div>
                        <label className="text-sm font-medium">Razón social / Nombre comercial *</label>
                        <input type="text" value={editData.businessName} onChange={e => setEditData({ ...editData, businessName: e.target.value })}
                          required className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1" data-testid="biz-name" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-sm font-medium">CUIT *</label>
                          <input type="text" inputMode="numeric" value={editData.cuit} onChange={e => setEditData({ ...editData, cuit: e.target.value })}
                            placeholder="20-12345678-9" required className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1" data-testid="biz-cuit" />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Condición IVA *</label>
                          <select value={editData.condicionIva || 'Responsable Inscripto'} onChange={e => setEditData({ ...editData, condicionIva: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1 bg-white" data-testid="biz-iva">
                            <option>Responsable Inscripto</option>
                            <option>Monotributo</option>
                            <option>Exento</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="text-sm font-medium">Teléfono</label>
                    <input type="tel" value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium">Calle <span className="text-rose-600">*</span></label>
                      <input type="text" value={editData.address}
                        onChange={e => setEditData({ ...editData, address: e.target.value })}
                        placeholder="Av. San Martín"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1"
                        data-testid="account-address" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Altura/Número <span className="text-rose-600">*</span></label>
                      <input type="text" inputMode="numeric" value={editData.addressNumber || ''}
                        onChange={e => setEditData({ ...editData, addressNumber: e.target.value })}
                        placeholder="1234"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1"
                        data-testid="account-address-number" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Ciudad *</label>
                    <select value={deliveryCities.includes((editData.city || '').toUpperCase()) ? (editData.city || '').toUpperCase() : (editData.city ? '__OTRA__' : '')}
                      onChange={(e) => {
                        if (e.target.value === '__OTRA__') setEditData({ ...editData, city: '' });
                        else setEditData({ ...editData, city: e.target.value });
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-1 bg-white" data-testid="city-select">
                      <option value="">— Elegí tu ciudad —</option>
                      {deliveryCities.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="__OTRA__">Otra ciudad (sin reparto)</option>
                    </select>
                    {(!editData.city || !deliveryCities.includes((editData.city || '').toUpperCase())) && (
                      <>
                        {editData.city !== undefined && !deliveryCities.includes((editData.city || '').toUpperCase()) && editData.city !== '' && (
                          <input type="text" value={editData.city} onChange={e => setEditData({ ...editData, city: e.target.value })}
                            placeholder="Cargá tu ciudad manualmente" className="w-full px-3 py-2 border border-slate-300 rounded-lg mt-2" />
                        )}
                        {(editData.city && !deliveryCities.includes((editData.city || '').toUpperCase())) && (
                          <p className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800" data-testid="no-delivery-warning">
                            ⚠️ Por ahora <strong>no llegamos con reparto</strong> a "{editData.city}". Podés retirar tus pedidos por nuestro depósito.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button onClick={saveUserData} disabled={savingProfile} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-white" style={{ backgroundColor: theme.primary, opacity: savingProfile ? 0.7 : 1 }} data-testid="btn-save-profile">
                      <Save size={16} />{savingProfile ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button onClick={() => setEditingData(false)} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-slate-300">
                      <X size={16} />Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="space-y-2">
                    {userData.name && <p className="text-sm"><strong>Nombre:</strong> {userData.name}</p>}
                    {userData.businessName && <p className="text-sm"><strong>Negocio:</strong> {userData.businessName}</p>}
                    {userData.phone && <p className="text-sm flex items-center gap-1"><Phone size={14} />{userData.phone}</p>}
                    {userData.address && <p className="text-sm flex items-center gap-1"><MapPin size={14} />{userData.address}, {userData.city}</p>}
                    {!userData.name && !userData.phone && !userData.address && (
                      <p className="text-sm" style={{ color: theme.textSecondary }}>No tenés datos guardados aún</p>
                    )}
                  </div>
                  <button onClick={startEditing} data-testid="btn-edit-profile" className="mt-3 flex items-center gap-2 text-sm font-medium" style={{ color: theme.primary }}>
                    <Edit2 size={14} />Editar mis datos
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cuenta Corriente (histórica) — visible sólo si store-settings.showCustomerAccount===true.
            Lo apagamos por defecto hasta que el matching cliente-app esté
            consolidado, para evitar que un usuario vea saldos de OTRO cliente. */}
        {accountSummary?.linked && storeSettings?.showCustomerAccount && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: theme.border }} data-testid="account-summary-section">
            <button onClick={() => setShowAccount(!showAccount)} className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.primary}15` }}>
                <Building2 size={20} style={{ color: theme.primary }} />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium" style={{ color: theme.text }}>Mi Cuenta Corriente</p>
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  {accountSummary.saldo_actual > 0
                    ? <span className="text-red-600 font-bold">Saldo a pagar: {formatPrice(accountSummary.saldo_actual)}</span>
                    : accountSummary.saldo_actual < 0
                      ? <span className="text-emerald-600 font-bold">Saldo a favor: {formatPrice(Math.abs(accountSummary.saldo_actual))}</span>
                      : <span className="text-emerald-600 font-bold">Cuenta al día ✓</span>}
                  {' · '}{accountSummary.totals?.facturas_count || 0} facturas · {accountSummary.totals?.recibos_count || 0} pagos
                </p>
              </div>
              {showAccount ? <ChevronUp size={20} style={{ color: theme.textSecondary }} /> : <ChevronDown size={20} style={{ color: theme.textSecondary }} />}
            </button>

            {showAccount && (
              <div className="border-t" style={{ borderColor: theme.border }}>
                {/* Saldo destacado */}
                <div className="p-4 grid grid-cols-3 gap-2 border-b" style={{ borderColor: theme.border, backgroundColor: '#FAFAFA' }}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: theme.textSecondary }}>Saldo actual</p>
                    <p className={`text-lg font-black tabular-nums mt-0.5 ${accountSummary.saldo_actual > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="account-saldo">
                      {formatPrice(accountSummary.saldo_actual)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: theme.textSecondary }}>Pendiente</p>
                    <p className="text-lg font-black tabular-nums mt-0.5 text-amber-600">
                      {accountSummary.totals?.pendientes_count || 0} <span className="text-xs font-normal">fact.</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: theme.textSecondary }}>Pagado</p>
                    <p className="text-lg font-black tabular-nums mt-0.5" style={{ color: theme.text }}>
                      {formatPrice(accountSummary.totals?.recibos_total || 0)}
                    </p>
                  </div>
                </div>

                {/* Tabs internas: Facturas vs Pagos */}
                <AccountTabs summary={accountSummary} formatPrice={formatPrice} formatDate={formatDate} theme={theme} />
              </div>
            )}
          </div>
        )}

        {/* Orders Section */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: theme.border }}>
          <button onClick={() => setShowOrders(!showOrders)} className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.primary}15` }}>
              <Package size={20} style={{ color: theme.primary }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: theme.text }}>Mis Pedidos</p>
              <p className="text-sm" style={{ color: theme.textSecondary }}>{stats.totalOrders} pedidos realizados</p>
            </div>
            {showOrders ? <ChevronUp size={20} style={{ color: theme.textSecondary }} /> : <ChevronDown size={20} style={{ color: theme.textSecondary }} />}
          </button>
          
          {showOrders && (
            <div className="border-t" style={{ borderColor: theme.border }}>
              {orders.length === 0 ? (
                <p className="p-4 text-center text-sm" style={{ color: theme.textSecondary }}>No tenés pedidos aún</p>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {orders.map((order, index) => {
                    const status = order.status || 'pendiente';
                    const statusInfo = STATUS_MAP[status] || STATUS_MAP.pendiente;
                    return (
                    <button key={index} onClick={() => setSelectedOrder(order)}
                      className="w-full p-4 border-b last:border-b-0 hover:bg-slate-50 text-left flex items-center gap-3"
                      style={{ borderColor: theme.border }}
                      data-testid={`order-row-${order.orderNumber}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-medium text-sm" style={{ color: theme.text }}>#{order.orderNumber}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.color}`}>{statusInfo.label}</span>
                          {order.order_type === 'vendedor' && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">Vendedor</span>}
                        </div>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>
                          {formatDate(order.timestamp || order.date)} - {order.items?.length || 0} productos
                          {order.seller_client_name && ` - ${order.seller_client_name}`}
                        </p>
                      </div>
                      <p className="font-bold" style={{ color: theme.primary }}>{formatPrice(order.total)}</p>
                      <Eye size={16} style={{ color: theme.textSecondary }} />
                    </button>
                  )})}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Favorites */}
        <button onClick={() => navigate('/products?filter=favorites')}
          className="w-full bg-white rounded-xl shadow-sm border overflow-hidden flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
          style={{ borderColor: theme.border }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
            <Heart size={20} fill="#EF4444" stroke="#EF4444" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium" style={{ color: theme.text }}>Mis Favoritos</p>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Productos que marcaste con ❤️</p>
          </div>
          <ChevronRight size={20} style={{ color: theme.textSecondary }} />
        </button>

        {/* Admin Section */}
        {isAdmin ? (
          <div className="space-y-4">
            <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: theme.text }}>
              <Shield size={20} style={{ color: theme.primary }} />Administración
            </h3>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: theme.border }}>
              {adminMenuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button key={index} onClick={item.action}
                    className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors border-b last:border-b-0"
                    style={{ borderColor: theme.border }}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${item.color}20` }}>
                      <Icon size={20} style={{ color: item.color }} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium" style={{ color: theme.text }}>{item.label}</p>
                      <p className="text-sm" style={{ color: theme.textSecondary }}>{item.description}</p>
                    </div>
                    <ChevronRight size={20} style={{ color: theme.textSecondary }} />
                  </button>
                );
              })}
            </div>
            <button onClick={handleAdminLogout}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-red-200 text-red-500 hover:bg-red-50">
              <LogOut size={18} />Cerrar sesión de administrador
            </button>
          </div>
        ) : (
          <button onClick={handleAdminLogin}
            className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed"
            style={{ borderColor: theme.border, color: theme.textSecondary }}>
            <Shield size={20} />Acceder como administrador
          </button>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => navigate('/quick-order')}
            className="p-4 rounded-xl text-white font-medium flex items-center justify-center gap-2"
            style={{ backgroundColor: theme.primary }}
            data-testid="quick-order-btn">
            <RefreshCw size={18} />Pedido Rápido
          </button>
          <button onClick={() => navigate('/products')}
            className="p-4 rounded-xl font-medium flex items-center justify-center gap-2 border-2"
            style={{ borderColor: theme.primary, color: theme.primary }}
            data-testid="view-catalog-btn">
            <Package size={18} />Ver Catálogo
          </button>
        </div>

        {/* Install App Button - always visible if not standalone */}
        {!isStandalone && (
          <button
            onClick={handleInstallApp}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border hover:bg-slate-50 transition-colors"
            style={{ borderColor: theme.border }}
            data-testid="install-app-btn"
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100">
              <Smartphone size={20} className="text-emerald-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium" style={{ color: theme.text }}>
                Agregar a la pantalla principal
              </p>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                {(installPrompt || window.__pwaPrompt) ? 'Tocá para instalar directo' : 'Acceso directo desde tu celular o escritorio'}
              </p>
            </div>
            <Download size={20} style={{ color: theme.primary }} />
          </button>
        )}
      </main>

      <CartSidebar />
      <BottomNavigation />
    </div>
  );
};

export default AccountPage;

// ====== Tabs de cuenta corriente (facturas / pagos) ======
const AccountTabs = ({ summary, formatPrice, formatDate, theme }) => {
  const [tab, setTab] = useState('facturas');
  const vouchers = summary?.vouchers || [];
  const receipts = summary?.receipts || [];
  return (
    <div>
      <div className="flex border-b" style={{ borderColor: theme.border }}>
        <button onClick={() => setTab('facturas')}
          className={`flex-1 px-3 py-2 text-xs font-bold transition ${tab === 'facturas' ? 'border-b-2' : 'opacity-60'}`}
          style={{ color: theme.text, borderColor: tab === 'facturas' ? theme.primary : 'transparent' }}
          data-testid="acc-tab-facturas">
          Facturas ({vouchers.length})
        </button>
        <button onClick={() => setTab('pagos')}
          className={`flex-1 px-3 py-2 text-xs font-bold transition ${tab === 'pagos' ? 'border-b-2' : 'opacity-60'}`}
          style={{ color: theme.text, borderColor: tab === 'pagos' ? theme.primary : 'transparent' }}
          data-testid="acc-tab-pagos">
          Pagos ({receipts.length})
        </button>
      </div>

      {tab === 'facturas' && (
        <div className="max-h-96 overflow-y-auto divide-y" style={{ borderColor: theme.border }}>
          {vouchers.length === 0 && <p className="p-4 text-center text-sm" style={{ color: theme.textSecondary }}>Sin facturas registradas.</p>}
          {vouchers.map((v, i) => (
            <div key={`${v.origen}-${v.tipo}-${v.comprobante}-${i}`}
              className="px-4 py-2.5 flex items-center gap-3"
              style={{ borderColor: theme.border }}
              data-testid={`acc-voucher-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded uppercase bg-zinc-100 text-zinc-700">{v.tipo}</span>
                  <span className="text-sm font-mono truncate" style={{ color: theme.text }}>{v.comprobante}</span>
                  {v.origen === 'legacy' && <span className="text-[9px] text-zinc-400 uppercase">histórica</span>}
                </div>
                <p className="text-[11px]" style={{ color: theme.textSecondary }}>{formatDate(v.fecha) || v.fecha}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums" style={{ color: theme.text }}>{formatPrice(v.total)}</p>
                {v.pendiente > 0 ? (
                  <p className="text-[11px] font-bold text-red-600 tabular-nums">Debe {formatPrice(v.pendiente)}</p>
                ) : (
                  <p className="text-[11px] font-bold text-emerald-600">✓ Pagada</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'pagos' && (
        <div className="max-h-96 overflow-y-auto divide-y" style={{ borderColor: theme.border }}>
          {receipts.length === 0 && <p className="p-4 text-center text-sm" style={{ color: theme.textSecondary }}>Sin pagos registrados.</p>}
          {receipts.map((r, i) => (
            <div key={`${r.numero_completo}-${i}`}
              className="px-4 py-2.5 flex items-center gap-3"
              style={{ borderColor: theme.border }}
              data-testid={`acc-receipt-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono truncate" style={{ color: theme.text }}>
                  {r.numero_completo || '—'}
                  {r.origen === 'legacy' && <span className="ml-1 text-[9px] text-zinc-400 uppercase">histórico</span>}
                </p>
                <p className="text-[11px]" style={{ color: theme.textSecondary }}>
                  {formatDate(r.fecha) || r.fecha}
                  {r.efectivo > 0 && ' · Efectivo'}
                  {r.transferencia > 0 && ' · Transf.'}
                  {r.cheque > 0 && ' · Cheque'}
                </p>
              </div>
              <p className="text-sm font-bold tabular-nums shrink-0 text-emerald-600">{formatPrice(r.total)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
